import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { stripViewSpecificAttributes } from 'dashboard/lib/workflows/steps-config-operations';
import {
  BlockInput,
  BlockParent,
  WorkflowBlock,
  WorkflowBlockAttributes,
} from 'dashboard/models/workflow-block';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { dashboardApi, LIST_TAG_ID } from 'dashboard/redux/api/api';
import { BlockData } from 'dashboard/redux/slices/workflow-block';
import { ApiOptions } from 'lib/clients/ApiClient';
import ApiResponseError from 'lib/errors/ApiResponseError';
import {
  ApiEndpoint,
  ApiListResponse,
  ApiModel,
  ApiResponse,
  PaginationParams,
} from 'lib/interfaces/api';

export const workflowBlockApi = dashboardApi.injectEndpoints({
  endpoints: (builder) => ({
    listWorkflowBlocks: builder.query<ApiListResponse<WorkflowBlock>, ApiOptions>({
      query: (options) =>
        SessionClientDashboard.listResource<WorkflowBlock, PaginationParams>(
          ApiEndpoint.WorkflowBlock,
          options,
        ),
      providesTags: (_result, _error) => {
        return [{ type: ApiModel.WorkflowBlock, id: LIST_TAG_ID }];
      },
    }),
    getWorkflowBlock: builder.query<ApiResponse<WorkflowBlock>, string>({
      query: (id) =>
        SessionClientDashboard.getResource<WorkflowBlock>(ApiEndpoint.WorkflowBlock, id),
      providesTags: (_result, _error, id) => {
        return [{ type: ApiModel.WorkflowBlock, id }];
      },
    }),
    createWorkflowBlock: builder.mutation<
      ApiResponse<WorkflowBlock>,
      { sourceBlockId: string; parentType: BlockParent['type']; parentId: string }
    >({
      query: async ({ sourceBlockId, parentType, parentId }) =>
        SessionClientDashboard.postResourceCreate<
          WorkflowBlock,
          {
            type: ApiModel.WorkflowBlock;
          }
        >(
          {
            type: ApiModel.WorkflowBlock,
          },
          {
            meta: {
              'src-workflow-block-id': sourceBlockId,
              'parent-type': parentType,
              'parent-id': parentId,
            },
          },
        ),
      invalidatesTags: (_result, _error, { parentType, parentId }) => {
        return [
          { type: ApiModel.WorkflowBlock, id: LIST_TAG_ID },
          { type: parentType, id: LIST_TAG_ID },
          { type: parentType, id: parentId },
        ];
      },
    }),
    updateWorkflowBlock: builder.mutation<
      ApiResponse<WorkflowBlock>,
      {
        id: string;
        inputs?: BlockInput[];
        stepsConfig?: WorkflowStep[];
      } & Partial<BlockData>
    >({
      query: async ({ id, inputs, stepsConfig, outputs, description }) => {
        const updatedAttributes: Partial<WorkflowBlockAttributes> = {};
        // these are the only attributes that are updatable
        if (inputs) {
          updatedAttributes.inputs = inputs;
        }
        if (stepsConfig) {
          updatedAttributes['steps-config'] = stripViewSpecificAttributes(stepsConfig);
        }
        if (outputs) {
          updatedAttributes.outputs = outputs;
        }
        if (description) {
          updatedAttributes.description = description;
        }
        return SessionClientDashboard.updateResource<WorkflowBlock>({
          type: ApiModel.WorkflowBlock,
          id,
          attributes: updatedAttributes,
        });
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          if (err instanceof ApiResponseError) {
            const title = err?.errors?.[0].title;
            const message = err?.errors?.[0].details;
            if (title && message) {
              console.error(`Error saving workflow block. ${title}: ${message}`);
            } else {
              console.error('Error saving workflow block', JSON.stringify(err, null, 2));
            }
          }
          throw err;
        }
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [
          { type: ApiModel.WorkflowBlock, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowBlock, id },
        ];
      },
    }),
    publishWorkflowBlock: builder.mutation<
      ApiResponse<WorkflowBlock>,
      {
        id: string;
        parentType: BlockParent['type'];
        parentId: string;
      }
    >({
      query: async ({ id }) =>
        SessionClientDashboard.postResourceAction<WorkflowBlock>(
          { id, type: ApiModel.WorkflowBlock },
          'publish',
        ),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          if (err instanceof ApiResponseError) {
            const title = err?.errors?.[0].title;
            const message = err?.errors?.[0].details;
            if (title && message) {
              console.error(`Error publishing workflow block. ${title}: ${message}`);
            } else {
              console.error('Error publishing workflow block', JSON.stringify(err, null, 2));
            }
          }
          throw err;
        }
      },
      invalidatesTags: (_result, _error, { id, parentType, parentId }) => {
        return [
          { type: ApiModel.WorkflowBlock, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowBlock, id },
          { type: parentType, id: parentId },
          { type: parentType, id: LIST_TAG_ID },
        ];
      },
    }),
    discardWorkflowBlock: builder.mutation<
      ApiResponse<WorkflowBlock>,
      { id: string; parentType: BlockParent['type']; parentId: string }
    >({
      query: async ({ id }) =>
        SessionClientDashboard.deleteResource<WorkflowBlock>(ApiEndpoint.WorkflowBlock, id),
      invalidatesTags: (_result, _error, { parentType, parentId }) => {
        return [
          { type: ApiModel.WorkflowBlock, id: LIST_TAG_ID },
          { type: parentType, id: parentId },
        ];
      },
    }),
  }),
});

// Hooks are autogenerated based on the defined endpoints
export const {
  useLazyListWorkflowBlocksQuery,
  useGetWorkflowBlockQuery,
  useLazyGetWorkflowBlockQuery,
  useCreateWorkflowBlockMutation,
  useDiscardWorkflowBlockMutation,
  endpoints: workflowBlockEndpoints,
} = workflowBlockApi;
